import { useTranslations } from 'i18n';
import { ActivityProcessingStatus, isActivityProcessing } from './processing-status-button';
import { DATE_FNS_SHORT_DATE_TIME_FORMAT, useDateFormatter } from 'shared';
import styles from './processing-status.module.css';
import { Text, Tag, TagThemeColor, Spinner } from '@SBGSports/catapult-core';
import { ActivityStatus } from './activity-status-util';
import { useGetActivity } from 'api/activities/get';

interface ActivityStatusRowProps {
    activityStatus: ActivityStatus;
}

const variants: Record<ActivityProcessingStatus, string> = {
    processing: 'warning',
    processed: 'success',
    error: 'error',
};

function getStatusVariant(status: string): ActivityProcessingStatus | '' {
    if (status.includes('error')) {
        return 'error';
    } else if (status.includes('baked')) {
        return 'processed';
    } else if (isActivityProcessing(status)) {
        return 'processing';
    }

    return '';
}

export const ActivityStatusRow: React.FC<ActivityStatusRowProps> = (props) => {
    const { activityStatus: status } = props;

    const { __ } = useTranslations();
    const dateFormatter = useDateFormatter();

    const { data: activity } = useGetActivity(status.activityId);

    const labels: Record<ActivityProcessingStatus, string> = {
        processing: __('data_processing.processing_status'),
        processed: __('data_processing.last_processed_status'),
        error: __('data_processing.processing_error_status'),
    };

    if (!activity) {
        return <></>;
    }

    const activityName = activity?.name ?? '';

    if (!activityName || activityName.length === 0) {
        return <> </>;
    }

    const statusVariant = getStatusVariant(status.status);

    const formattedActivityDate = dateFormatter(activity?.start_time ?? new Date(), DATE_FNS_SHORT_DATE_TIME_FORMAT);

    const formattedModifiedDate = dateFormatter(
        new Date(Date.parse(status.modifiedDate ?? '')),
        DATE_FNS_SHORT_DATE_TIME_FORMAT,
    );

    return (
        <div className={styles['activity-row']}>
            <div className={styles['left-cell']}>
                <Text variant="label-large" className="font-semibold">
                    {activityName}
                </Text>
                <Text variant="label-large" className="pt-1 text-system-on-surface-variant">
                    {formattedActivityDate}
                </Text>
            </div>
            <div className={styles['mid-cell']}>
                <Tag
                    color={variants[statusVariant as ActivityProcessingStatus] as TagThemeColor}
                    label={labels[statusVariant as ActivityProcessingStatus]}
                    size="md"
                    className="px-2 py-1"
                    testId={variants[statusVariant as ActivityProcessingStatus].concat('-tag')}
                />
                <Text variant="label-large" className="pt-1 text-system-on-surface-variant">
                    {formattedModifiedDate}
                </Text>
            </div>
            <div className={styles['right-cell']}>
                {statusVariant === 'processing' && <Spinner id={`processing-spinner-${activity?.id}`} />}
            </div>
        </div>
    );
};
